import React, { useMemo, Fragment, useState, useEffect } from 'react';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FolderRounded from '@mui/icons-material/FolderRounded';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem, treeItemClasses } from '@mui/x-tree-view/TreeItem';
import Collapse from '@mui/material/Collapse';
import { animated, useSpring } from '@react-spring/web';
import DescriptionIcon from '@mui/icons-material/Description';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import Tooltip from '@mui/material/Tooltip';
import MpgConfirmDialog from "../../../../../common/dialog/MpgConfirmDialog";
import { v4 as uuidv4 } from 'uuid';
import AddFolderActionDialog from "./AddFolderActionDialog";
import AddSubFolderActionDialog from "./AddSubFolderActionDialog";
import EditFolderActionDialog from "./EditFolderActionDialog";
import EditSubFolderActionDialog from "./EditSubFolderActionDialog";
import ManagePDFilesForFolderDialog from "./ManagePDFilesForFolderDialog";
import ManagePDFilesForSubFolderDialog from "./ManagePDFilesForSubFolderDialog";
import truncate from "lodash/truncate";
import ManagePDFilesForRootDialog from "./ManagePDFilesForRootDialog";
import { useOutletContext } from "react-router-dom";
import { resourceSortWindowsFormat } from "../../../../../hooksAndFunctions/functions/resourceSortWindowsFormat";

const FolderTreeStructureComp = ({ pdDataByClientAccId, compositeFolderArrayInsideRoot, setCompositeFolderArrayInsideRoot, rootFolderObject, setRootFolderObject }) => {
    const [openPDFilesForRootDialog, setOpenPDFilesForRootDialog] = useState(false)
    const [openPDFilesForFolderDialog, setOpenPDFilesForFolderDialog] = useState(false)
    const [openPDFilesForSubFolderDialog, setOpenPDFilesForSubFolderDialog] = useState(false)

    const [addFolderActionOpen, setAddFolderActionOpen] = useState(false)
    const [addSubFolderActionOpen, setAddSubFolderActionOpen] = useState(false)

    const [editFolderActionOpen, setEditFolderActionOpen] = useState(false)
    const [editSubFolderActionOpen, setEditSubFolderActionOpen] = useState(false)

    const [deleteFolderActionOpen, setDeleteFolderActionOpen] = useState(false)
    const [deleteSubFolderActionOpen, setDeleteSubFolderActionOpen] = useState(false)
    const [deleteFolderFileOpen, setDeleteFolderFileOpen] = useState(false)
    const [deleteSubFolderFileOpen, setDeleteSubFolderFileOpen] = useState(false)
    const [deleteRootFolderFileOpen, setDeleteRootFolderFileOpen] = useState(false)

    const ClientAccountName = pdDataByClientAccId?.ClientAccountName
    const programDescriptorList = pdDataByClientAccId?.ProgramDescriptors || []
    
    /** get only active resources irrespective of type*/
    const listOfActiveResources = !!programDescriptorList && programDescriptorList.filter((eachItem) => {
        if (!!eachItem?.IsActive) {
            return eachItem
        }
    })

    const DotIcon = () => {
        return (
            <DescriptionIcon sx={{ color: 'warning.main', fontSize: "15px", marginRight: 1 }} />
        );
    }

    const StyledTreeItemLabel = styled(Typography)({
        color: 'inherit',
        fontFamily: 'inherit',
        fontWeight: 'inherit',
        flexGrow: 1,
    });

    const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
        color:
            theme.palette.mode === 'light'
                ? theme.palette.grey[800]
                : theme.palette.grey[400],
        position: 'relative',
        [`& .${treeItemClasses.content}`]: {
            flexDirection: 'row-reverse',
            borderRadius: theme.spacing(0.7),
            marginBottom: theme.spacing(0.5),
            marginTop: theme.spacing(0.5),
            padding: theme.spacing(0.5),
            paddingRight: theme.spacing(1),
            fontWeight: 500,
            [`& .${treeItemClasses.label}`]: {
                fontWeight: 'inherit',
            },
            [`& .${treeItemClasses.iconContainer}`]: {
                marginRight: theme.spacing(2),
            },
            [`&.Mui-expanded `]: {
                '&:not(.Mui-focused, .Mui-selected, .Mui-selected.Mui-focused) .labelIcon': {
                    color:
                        theme.palette.mode === 'light'
                            ? theme.palette.primary.main
                            : theme.palette.secondary.dark,
                },
                '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    left: '16px',
                    top: '44px',
                    height: 'calc(100% - 48px)',
                    width: '1.5px',
                    backgroundColor:
                        theme.palette.mode === 'light'
                            ? theme.palette.grey[300]
                            : theme.palette.grey[700],
                },
            },
            '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.mode === 'light' ? theme.palette.primary.main : 'white',
            },
            [`&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused`]: {
                backgroundColor:
                    theme.palette.mode === 'light'
                        ? alpha(theme.palette.primary.main, 0.1)
                        : theme.palette.secondary.dark,
                color: theme.palette.primary.main,
            },
        },
        [`& .${treeItemClasses.group}`]: {
            marginLeft: theme.spacing(3.5),
            [`& .${treeItemClasses.content}`]: {
                fontWeight: 500,
            },
        },
    }));

    const AnimatedCollapse = animated(Collapse);

    function TransitionComponent(props) {
        const style = useSpring({
            to: {
                opacity: props.in ? 1 : 0,
                transform: `translate3d(0,${props.in ? 0 : 20}px,0)`,
            },
        });

        return <AnimatedCollapse style={style} {...props} />;
    }

    /**
     * 
     */
    const [parentItemObjData, setParentItemObjData] = useState({})
    const [targetItemObjData, setTargetItemObjData] = useState({})
    const [subFolderInfoData, setSubFolderInfoData] = useState({})

    /** typeExpected = "rootFolder"|"foldr"|"subFolder"|"file" */
    const StyledTreeItem = React.forwardRef(function StyledTreeItem(props, ref) {
        const { labelIcon: LabelIcon, labelText, isActivePDFile, typeExpected, parentItemdata, subFolderdata, targetItemData, ...other } = props;

        let inActivePDFile = false
        if (typeExpected === "rootFolderFile" || typeExpected === "folderFile" || typeExpected === "subFolderFile") {
            if (!isActivePDFile) {
                inActivePDFile = true
            }
        }
        return (
            <StyledTreeItemRoot
                label={
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            component={LabelIcon}
                            className="labelIcon"
                            color="inherit"
                            sx={{ mr: 1, fontSize: '1.2rem' }}
                        />
                        <Tooltip title={`${labelText}`}>
                            <StyledTreeItemLabel
                                align="left"
                                variant="body2"
                                sx={{
                                    color: !!inActivePDFile ? "red" : "",
                                }}
                            >{
                                    truncate(
                                        `${labelText}`, {
                                        'length': 30,
                                        'omission': "..."
                                    })
                                }
                            </StyledTreeItemLabel>
                        </Tooltip>

                        {/** add folder */}
                        {
                            !!typeExpected && (typeExpected === "root" || typeExpected === "folder") && (
                                <Tooltip title={`${typeExpected === "root" ? fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.AddFolders') : fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.AddSubFolders')}`}>
                                    <IconButton
                                        aria-label="add-pd-folder"
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            setParentItemObjData(parentItemdata)
                                            setTargetItemObjData(targetItemData)

                                            if (typeExpected === "root") {
                                                setAddFolderActionOpen(true)
                                            } else if (typeExpected === "folder") {
                                                setAddSubFolderActionOpen(true)
                                            }
                                        }}
                                    >
                                        <CreateNewFolderIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )
                        }

                        {/* add pd files */}
                        {
                            !!typeExpected && (typeExpected === "root" || typeExpected === "folder" || typeExpected === "subFolder") && (

                                <Tooltip title= {fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.AddResourceFiles')}>
                                    <IconButton
                                        aria-label="add-pd-files"
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            setParentItemObjData(parentItemdata)
                                            setTargetItemObjData(targetItemData)

                                            if (typeExpected === "root") {
                                                setOpenPDFilesForRootDialog(true);
                                            } else if (typeExpected === "folder") {
                                                setOpenPDFilesForFolderDialog(true);
                                            } else if (typeExpected === "subFolder") {
                                                setOpenPDFilesForSubFolderDialog(true)
                                            }
                                        }}
                                    >
                                        <NoteAddIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                        {/* edit folder/subfolder names */}
                        {
                            !!typeExpected && (typeExpected === "folder" || typeExpected === "subFolder") && (
                                <Tooltip title={`${typeExpected === "folder" ? fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.EditFolderName') : fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.EditSubFolderName')}`}>
                                    <IconButton
                                        aria-label="edit-pd-folder"
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            setParentItemObjData(parentItemdata)
                                            setTargetItemObjData(targetItemData)

                                            if (typeExpected === "folder") {
                                                setEditFolderActionOpen(true)
                                            } else if (typeExpected === "subFolder") {
                                                setEditSubFolderActionOpen(true)
                                            }
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )
                        }

                        {/* delete folder/subfolder/pdfiles */}
                        {
                            !!typeExpected && (typeExpected !== "root") && (
                                <Tooltip title={`${typeExpected === "folder" ? fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.DeleteFolder') : typeExpected === "subFolder" ? fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.DeleteSubFolder') : fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.RemoveResourceFile') }`}>
                                    <IconButton
                                        aria-label="delete-pd-folder"
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            setParentItemObjData(parentItemdata)
                                            setTargetItemObjData(targetItemData)

                                            if (!!typeExpected && typeExpected === "folder") {
                                                setDeleteFolderActionOpen(true)
                                            }
                                            else if (!!typeExpected && typeExpected === "subFolder") {
                                                setDeleteSubFolderActionOpen(true)
                                            }
                                            else if (!!typeExpected && typeExpected === "folderFile") {
                                                setDeleteFolderFileOpen(true)
                                            }
                                            else if (!!typeExpected && typeExpected === "subFolderFile") {
                                                /**need this extra info to remove subfolder file */
                                                setSubFolderInfoData(subFolderdata)
                                                setDeleteSubFolderFileOpen(true)
                                            }
                                            else if (!!typeExpected && typeExpected === "rootFolderFile") {
                                                setDeleteRootFolderFileOpen(true)
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                    </Box>
                }
                {...other}
                TransitionComponent={TransitionComponent}
                ref={ref}
            />
        );
    });

    /**root folder file details */
    const listOfFilesInRoot = rootFolderObject?.ProgramDescriptors || []

    // console.log("listOfFilesInRoot", rootFolderObject)

    const emptyRootFolder = (
        <StyledTreeItem
            nodeId="00"
            labelText={`${ClientAccountName}`}
            labelIcon={FolderRounded}
            typeExpected='root'
        />
    )
    const folderTreeWithData = (
        <Fragment>
            <StyledTreeItem
                nodeId="00"
                labelText={`${ClientAccountName}`}
                labelIcon={FolderRounded}
                typeExpected='root'
            >
               
                {/** folder list inside root*/}
                {
                    !!compositeFolderArrayInsideRoot && !!compositeFolderArrayInsideRoot.length && compositeFolderArrayInsideRoot.map((eachFolder, folderIndex) => {
                        const folderId = eachFolder?.Id
                        const folderName = eachFolder?.ResourceFolderName
                        const programDescriptorsList = eachFolder?.ProgramDescriptors    
                        programDescriptorsList.sort(windowsSortForProgramDescriptor);
                        const subFolderList = eachFolder?.ResourceSubFolders         
                        subFolderList.sort(windowsSortForResourceFolder);
                        const isDeletedForFolder = eachFolder?.IsDeleted

                        return (
                            !isDeletedForFolder && (
                                <StyledTreeItem
                                    nodeId={uuidv4()}
                                    labelText={folderName}
                                    labelIcon={FolderRounded}
                                    typeExpected="folder"
                                    parentItemdata={eachFolder}
                                    targetItemData={eachFolder}
                                    key={`${folderId}-${folderIndex}`}
                                >
                                   

                                    {/* display subfolder */}
                                    {
                                        !!subFolderList && !!subFolderList.length && subFolderList.map((eachSubFolder, subfolderIndex) => {
                                            const subFolderId = eachSubFolder?.Id
                                            const subFolderName = eachSubFolder?.ResourceSubFolderName
                                            const subFolderPDList = eachSubFolder?.ProgramDescriptors
                                            subFolderPDList.sort(windowsSortForProgramDescriptor);
                                            const isDeletedForSubFolder = eachSubFolder?.IsDeleted

                                            return (
                                                !isDeletedForSubFolder && (
                                                    <StyledTreeItem
                                                        nodeId={uuidv4()}
                                                        labelText={subFolderName}
                                                        labelIcon={FolderRounded}
                                                        typeExpected="subFolder"
                                                        parentItemdata={eachFolder}
                                                        targetItemData={eachSubFolder}
                                                        key={`${subFolderId}-${subfolderIndex}`}
                                                    >
                                                        {
                                                            !!subFolderPDList && !!subFolderPDList.length && subFolderPDList.map((eachSubFolderPD, subfolderPdIndex) => {

                                                                const subFolderPDId = eachSubFolderPD?.ProgramDescripitorId

                                                                const subFolderPDLabel = eachSubFolderPD?.ClientAccountProgramDescripitorTitle

                                                                const subfolderPDIsActive = eachSubFolderPD?.IsActive

                                                                return (
                                                                    <StyledTreeItem
                                                                        nodeId={uuidv4()}
                                                                        labelText={subFolderPDLabel}
                                                                        labelIcon={DotIcon} typeExpected='subFolderFile'
                                                                        parentItemdata={eachFolder}
                                                                        subFolderdata={eachSubFolder}
                                                                        targetItemData={eachSubFolderPD}
                                                                        key={`${subFolderPDId}-${subfolderPdIndex}`}
                                                                        isActivePDFile={subfolderPDIsActive}
                                                                    />
                                                                )
                                                            })
                                                        }
                                                    </StyledTreeItem>
                                                )
                                            )
                                        })
                                    }
                                     {/* display PD files */}
                                     {
                                        !!programDescriptorsList && !!programDescriptorsList.length && programDescriptorsList.map((eachFolderPD, folderPdIndex) => {

                                            const pdId = eachFolderPD?.ProgramDescripitorId

                                            const pdLabel = eachFolderPD?.ClientAccountProgramDescripitorTitle

                                            const folderPDIsActive = eachFolderPD?.IsActive
                                            return (
                                                <StyledTreeItem
                                                    nodeId={uuidv4()}
                                                    labelText={pdLabel}
                                                    labelIcon={DotIcon}
                                                    typeExpected='folderFile'
                                                    parentItemdata={eachFolder}
                                                    targetItemData={eachFolderPD}
                                                    key={pdId} // Use pdId instead of index
                                                    isActivePDFile={folderPDIsActive}
                                                />
                                            )
                                        })
                                    }
                                </StyledTreeItem>
                            )
                        )
                    })
                }
                 {/** pd file list inside root*/}
                 {
                    !!listOfFilesInRoot && listOfFilesInRoot.map((eachRootPDFile, rootPdIndex) => {
                        const pdId = eachRootPDFile?.ProgramDescripitorId
                        const pdLabel = eachRootPDFile?.ClientAccountProgramDescripitorTitle
                        const rootPDIsActive = eachRootPDFile?.IsActive

                        return (
                            <StyledTreeItem
                                nodeId={uuidv4()}
                                labelText={pdLabel}
                                labelIcon={DotIcon}
                                typeExpected='rootFolderFile'
                                parentItemdata={rootFolderObject}
                                targetItemData={eachRootPDFile}
                                key={pdId} // Use pdId instead of index
                                isActivePDFile={rootPDIsActive}
                            />
                        )
                    })
                }
            </StyledTreeItem>
        </Fragment>
    )

    const resourcesCMSData = useOutletContext();

    const fetchClientAccResDtlsLabel = (key) => {
        if (resourcesCMSData && resourcesCMSData?.pageContent["ClientAccountResourcesDetails"]) {
          const resourcesCMSDataLabel = resourcesCMSData.pageContent["ClientAccountResourcesDetails"];
          return returnLabel(resourcesCMSDataLabel?.Labels, key)
        }
    }

    // const fetchCommonCMSLabel = (key) => {
    //     if (resourcesCMSData && resourcesCMSData?.pageContent["CommonComponent"]) {
    //         const commonCMSDataLabel = resourcesCMSData.pageContent["CommonComponent"];
    //         return returnLabel(commonCMSDataLabel?.Labels, key)
    //     }
    //     return '';
    // }

    const returnLabel = (labelArray, key) => {
        if (labelArray.length > 0 && key) {
            return labelArray.find((x) => x.Key === key)?.Phrase || '';
        }
        return '';
    }

    // Usage for folders:
      function windowsSortForResourceFolder(a, b) {
        return resourceSortWindowsFormat(a, b, 'ResourceSubFolderName');
      }
    
      // Usage for program descriptors:
      function windowsSortForProgramDescriptor(a, b) {
        return resourceSortWindowsFormat(a, b, 'ProgramDescripitorTitle');
      }

    

    return (
        <Fragment>
            <Grid container justifyContent="center">
                <SimpleTreeView
                    aria-label="gmail"
                    defaultExpandedNodes={['00']}
                    defaultSelectedNodes="00"
                    sx={{
                        height: 'fit-content',
                        flexGrow: 1,
                        maxWidth: 500,
                        minHeight: 300,
                        overflowY: 'auto'
                    }}
                >
                    {
                        !compositeFolderArrayInsideRoot && emptyRootFolder
                    }
                    {
                        (!!compositeFolderArrayInsideRoot) && folderTreeWithData
                    }
                </SimpleTreeView>
            </Grid>


            <MpgConfirmDialog
                open={deleteFolderActionOpen}
                setOpen={setDeleteFolderActionOpen}
                //content={`Are you sure, you want to remove this folder? Please note: All the subfolders & files under this folder will be removed. `}
                content={fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.ConfirmMsgToRemoveFolder')}
                extraLogicHandlerOnAgree={(e) => {

                    /** for delete we need to update flag IsDeleted = true on selected folder */
                    const modifiedParentFolderObj = {
                        ...parentItemObjData,
                        IsDeleted: true
                    }
                    let newArray = []

                    !!compositeFolderArrayInsideRoot && compositeFolderArrayInsideRoot.forEach((eachItem, index) => {

                        if (eachItem?.Id === parentItemObjData?.Id) {
                            newArray.push(modifiedParentFolderObj)
                        } else {
                            newArray.push(eachItem)
                        }
                    })
                    setCompositeFolderArrayInsideRoot(newArray)
                    setDeleteFolderActionOpen(false)
                }}
            />
            <MpgConfirmDialog
                open={deleteSubFolderActionOpen}
                setOpen={setDeleteSubFolderActionOpen}
                //content={`Are you sure, you want to remove this subfolder? Please note: all files under this subfolder will be removed. `}
                content={fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.ConfirmMsgToRemoveSubFolder')}
                extraLogicHandlerOnAgree={(e) => {
                    /** for delete we need to update flag IsDeleted = true on selected sub-folder */
                    const subFolderList = parentItemObjData?.ResourceSubFolders
                    let modifiedSubFolderList = []
                    !!subFolderList && subFolderList.forEach((eachSubFolder) => {
                        if (eachSubFolder?.Id === targetItemObjData?.Id) {
                            modifiedSubFolderList.push({
                                ...eachSubFolder,
                                IsDeleted: true
                            })
                        } else {
                            modifiedSubFolderList.push(eachSubFolder)
                        }
                    })

                    const modifiedTargetSubFolderObj = {
                        ...parentItemObjData,
                        ResourceSubFolders: modifiedSubFolderList
                    }

                    let newArray = []

                    !!compositeFolderArrayInsideRoot && compositeFolderArrayInsideRoot.forEach((eachItem, index) => {

                        if (eachItem?.Id === parentItemObjData?.Id) {
                            newArray.push(modifiedTargetSubFolderObj)
                        } else {
                            newArray.push(eachItem)
                        }
                    })

                    setCompositeFolderArrayInsideRoot(newArray)
                    setDeleteSubFolderActionOpen(false)
                }}
            />

            {/**delete alert and action for files from root folder */}
            <MpgConfirmDialog
                open={deleteRootFolderFileOpen}
                setOpen={setDeleteRootFolderFileOpen}
                //content='Are you sure, you want to delete this resource file from the root folder ?'
                content= {fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.ConfirmMsgToDeleteFileInRootFolder')}
                extraLogicHandlerOnAgree={(e) => {

                    /**we need to filter out file itself*/

                    const fileIdTobeDeleted = targetItemObjData?.ProgramDescripitorId

                    const rootFolderFileList = parentItemObjData?.ProgramDescriptors
                    const filteredFileList = !!rootFolderFileList && rootFolderFileList.filter((eachFile) => eachFile?.ProgramDescripitorId !== fileIdTobeDeleted)

                    const modifiedRootFolderObj = {
                        ...parentItemObjData,
                        ProgramDescriptors: filteredFileList
                    }

                    setRootFolderObject(modifiedRootFolderObj)
                    setDeleteRootFolderFileOpen(false)
                }}
            />
            {/**delete alert and action for files from folder */}
            <MpgConfirmDialog
                open={deleteFolderFileOpen}
                setOpen={setDeleteFolderFileOpen}
                //content='Are you sure, you want to delete this resource file from the folder ?'
                content={fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.ConfirmMsgToDeleteFileInFolder')}
                extraLogicHandlerOnAgree={(e) => {

                    /**we need to filter out file itself*/

                    const fileIdTobeDeleted = targetItemObjData?.ProgramDescripitorId

                    const parentFolderFileList = parentItemObjData?.ProgramDescriptors
                    const filteredFileList = !!parentFolderFileList && parentFolderFileList.filter((eachFile) => eachFile?.ProgramDescripitorId !== fileIdTobeDeleted)

                    const modifiedParentFolderObj = {
                        ...parentItemObjData,
                        ProgramDescriptors: filteredFileList
                    }

                    let newArray = []

                    !!compositeFolderArrayInsideRoot && compositeFolderArrayInsideRoot.forEach((eachItem, index) => {

                        if (eachItem?.Id === parentItemObjData?.Id) {
                            newArray.push(modifiedParentFolderObj)
                        } else {
                            newArray.push(eachItem)
                        }
                    })

                    setCompositeFolderArrayInsideRoot(newArray)
                    setDeleteFolderFileOpen(false)
                }}
            />

            <MpgConfirmDialog
                open={deleteSubFolderFileOpen}
                setOpen={setDeleteSubFolderFileOpen}
                //content='Are you sure, you want to delete this resource file from the subfolder?'
                content={fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.ConfirmMsgToDeleteFileInSubFolder')}
                extraLogicHandlerOnAgree={(e) => {

                    /**we need to filter out file itself*/
                    const fileIdTobeDeleted = targetItemObjData?.ProgramDescripitorId
                    const subFolderFileList = subFolderInfoData?.ProgramDescriptors
                    const filteredFileList = !!subFolderFileList && subFolderFileList.filter((eachFile) => eachFile?.ProgramDescripitorId !== fileIdTobeDeleted)

                    const subFolderListFromParentFolder = parentItemObjData?.ResourceSubFolders

                    /**adding modified filelist into subfolder */
                    let modifiedSubFolderList = []
                    !!subFolderListFromParentFolder && subFolderListFromParentFolder.forEach((eachSubFolder) => {
                        if (eachSubFolder?.Id === subFolderInfoData?.Id) {
                            modifiedSubFolderList.push({
                                ...eachSubFolder,
                                ProgramDescriptors: filteredFileList
                            })
                        } else {
                            modifiedSubFolderList.push(eachSubFolder)
                        }
                    })

                    /**adding modified subfolderlist into parentfolder */
                    const modifiedParentFolderObj = {
                        ...parentItemObjData,
                        ResourceSubFolders: modifiedSubFolderList
                    }

                    let newArray = []

                    !!compositeFolderArrayInsideRoot && compositeFolderArrayInsideRoot.forEach((eachItem, index) => {

                        if (eachItem?.Id === parentItemObjData?.Id) {
                            newArray.push(modifiedParentFolderObj)
                        } else {
                            newArray.push(eachItem)
                        }
                    })

                    setCompositeFolderArrayInsideRoot(newArray)
                    setDeleteSubFolderFileOpen(false)
                }}
            />

            <AddFolderActionDialog
                open={addFolderActionOpen}
                setOpen={setAddFolderActionOpen}

                compositeFolderArrayInsideRoot={compositeFolderArrayInsideRoot}
                setCompositeFolderArrayInsideRoot={setCompositeFolderArrayInsideRoot}
            />
            <AddSubFolderActionDialog
                open={addSubFolderActionOpen}
                setOpen={setAddSubFolderActionOpen}

                compositeFolderArrayInsideRoot={compositeFolderArrayInsideRoot}
                setCompositeFolderArrayInsideRoot={setCompositeFolderArrayInsideRoot}

                parentItemObjData={parentItemObjData}
                setParentItemObjData={setParentItemObjData}
                targetItemObjData={targetItemObjData}
                setTargetItemObjData={setTargetItemObjData}
            />

            <EditFolderActionDialog
                open={editFolderActionOpen}
                setOpen={setEditFolderActionOpen}

                compositeFolderArrayInsideRoot={compositeFolderArrayInsideRoot}
                setCompositeFolderArrayInsideRoot={setCompositeFolderArrayInsideRoot}

                parentItemObjData={parentItemObjData}
                setParentItemObjData={setParentItemObjData}
                targetItemObjData={targetItemObjData}
                setTargetItemObjData={setTargetItemObjData}
            />

            <EditSubFolderActionDialog
                open={editSubFolderActionOpen}
                setOpen={setEditSubFolderActionOpen}

                compositeFolderArrayInsideRoot={compositeFolderArrayInsideRoot}
                setCompositeFolderArrayInsideRoot={setCompositeFolderArrayInsideRoot}

                parentItemObjData={parentItemObjData}
                setParentItemObjData={setParentItemObjData}
                targetItemObjData={targetItemObjData}
                setTargetItemObjData={setTargetItemObjData}
            />

            <ManagePDFilesForRootDialog
                open={openPDFilesForRootDialog}
                setOpen={setOpenPDFilesForRootDialog}

                PDListFromAPI={listOfActiveResources || []}

                rootFolderObject={rootFolderObject}
                setRootFolderObject={setRootFolderObject}
            />

            <ManagePDFilesForFolderDialog
                open={openPDFilesForFolderDialog}
                setOpen={setOpenPDFilesForFolderDialog}

                PDListFromAPI={listOfActiveResources || []}

                parentItemObjData={parentItemObjData}
                targetItemObjData={targetItemObjData}

                compositeFolderArrayInsideRoot={compositeFolderArrayInsideRoot}
                setCompositeFolderArrayInsideRoot={setCompositeFolderArrayInsideRoot}
            />
            <ManagePDFilesForSubFolderDialog
                open={openPDFilesForSubFolderDialog}
                setOpen={setOpenPDFilesForSubFolderDialog}

                PDListFromAPI={listOfActiveResources || []}

                parentItemObjData={parentItemObjData}
                targetItemObjData={targetItemObjData}

                compositeFolderArrayInsideRoot={compositeFolderArrayInsideRoot}
                setCompositeFolderArrayInsideRoot={setCompositeFolderArrayInsideRoot}
            />
        </Fragment>
    );
}
export default FolderTreeStructureComp